import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import HelpTip from "@/components/HelpTip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  SITE_DEFAULTS,
  SLOT_DURATION_OPTIONS,
  TIMEZONES,
  AU_STATES,
} from "@/lib/constants";
import { createSite, getClientBufferFlag, listClients, updateSite, getSite } from "@/lib/api";
import { OpenDaysPicker } from "@/components/OpenDaysPicker";
import { DailyBreaksEditor } from "@/components/DailyBreaksEditor";
import { AuTimeInput } from "@/components/AuDateTime";

/**
 * SiteForm — create + edit dialog for lbs_sites.
 *
 * Props:
 *   open / onOpenChange — dialog control
 *   site — existing row (edit mode) or null (create)
 *   onSaved(savedSite) — fired after a successful insert/update
 */
/**
 * SiteForm — create + edit dialog for lbs_sites.
 *
 * Implementation note: the form body lives in <SiteFormInner /> and is
 * keyed on `site?.id || 'new'`, so React remounts (and so re-initialises
 * useState) whenever the editing target changes. This avoids a syncing
 * useEffect that the new react-hooks/set-state-in-effect rule rejects.
 */
export default function SiteForm({ open, onOpenChange, site, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="site-form-dialog">
        {open && (
          <SiteFormInner
            key={site?.id || "new"}
            site={site}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SiteFormInner({ site, onOpenChange, onSaved }) {
  const { hasRole, assignments } = useAuth();
  // Treat a site with no id (e.g. duplicate-source pre-fill) as a new record.
  const isEdit = !!site?.id;

  // Lazy initial state — no resync useEffect needed because the key prop
  // remounts this component whenever `site` changes.
  const initialClientId =
    site?.client_id ??
    (!isEdit && !hasRole(["super_admin"])
      ? assignments.find((a) => a.client_id)?.client_id ?? ""
      : "");

  const [values, setValues] = useState(() => ({
    ...SITE_DEFAULTS,
    ...(site || {}),
    client_id: initialClientId,
  }));
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // PR-1 buffer feature: show the field only when the parent client has the
  // master switch on. Cached lookup is cheap because we re-use `listClients`
  // which the form already loads for super_admins. For non-super_admins we
  // fetch once per form open.
  const [clientBufferEnabled, setClientBufferEnabled] = useState(false);

  useEffect(() => {
    if (!values.client_id) return undefined;
    let cancelled = false;
    (async () => {
      const flag = await getClientBufferFlag(values.client_id);
      if (!cancelled) setClientBufferEnabled(flag.enabled);
    })();
    return () => { cancelled = true; };
  }, [values.client_id]);

  // Always re-read the canonical row from the DB when the dialog opens.
  // This guarantees the form never displays a stale parent-state snapshot
  // (e.g. after a previous save when the parent's refetch hadn't completed).
  useEffect(() => {
    if (!site?.id) return undefined;
    let cancelled = false;
    (async () => {
      const { data } = await getSite(site.id);
      if (cancelled || !data) return;
      setValues((v) => ({ ...SITE_DEFAULTS, ...data, client_id: data.client_id }));
    })();
    return () => { cancelled = true; };
  }, [site?.id]);

  // Load client list (only super_admins need to pick on create).
  useEffect(() => {
    if (isEdit || !hasRole(["super_admin"])) return undefined;
    let cancelled = false;
    listClients().then(({ data }) => {
      if (!cancelled) setClients(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, hasRole]);

  const set = (k, v) => setValues((vs) => ({ ...vs, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!values.name?.trim()) {
      setError("Name is required");
      return;
    }
    if (!isEdit && !values.client_id) {
      setError("Pick a client / tenant");
      return;
    }
    if ((values.daily_breaks || []).some((b) => !b.start || !b.end || b.start >= b.end)) {
      setError("Each daily break needs a start time before its end time.");
      return;
    }
    if (values.operating_hours_start >= values.operating_hours_end) {
      setError("Operating hours: start must be before end");
      return;
    }

    setSaving(true);
    // Coerce numeric strings → numbers, blanks → null
    const payload = { ...values };
    ["min_booking_minutes", "max_booking_hours", "min_lead_time_hours",
      "max_horizon_days", "max_concurrent_bookings", "slot_duration_minutes",
      "booker_min_duration_minutes"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
        payload[k] = null;
      } else {
        const n = parseInt(payload[k], 10);
        payload[k] = Number.isFinite(n) ? n : null;
      }
    });
    // default_buffer_minutes is NOT NULL in the DB — coerce blanks to 0.
    // But only include it when the client has the buffer feature enabled; this
    // keeps pre-migration-150 DBs safe (column doesn't exist yet).
    if (clientBufferEnabled) {
      const raw = payload.default_buffer_minutes;
      if (raw === "" || raw === null || raw === undefined) {
        payload.default_buffer_minutes = 0;
      } else {
        const n = parseInt(raw, 10);
        payload.default_buffer_minutes = Number.isFinite(n) ? Math.max(0, Math.min(180, n)) : 0;
      }
    } else {
      delete payload.default_buffer_minutes;
    }
    const { data, error: err } = isEdit
      ? await updateSite(site.id, payload)
      : await createSite(payload);
    setSaving(false);

    if (err) {
      // Show the exact Postgres / Supabase error so silent failures stop
      setError(err.message || JSON.stringify(err));
      return;
    }
    toast.success(isEdit ? "Site saved" : "Site created");
    onSaved?.(data);
    onOpenChange(false);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit site" : "Add site"}</DialogTitle>
        <DialogDescription>
          Sites are physical locations under a client. Each site groups one or more loading bays.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <Alert variant="destructive" data-testid="site-form-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Section: Identity */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Identity</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="site-name">Name *</Label>
                <Input
                  id="site-name"
                  data-testid="site-name-input"
                  value={values.name}
                  onChange={(e) => set("name", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-code">Code</Label>
                <Input
                  id="site-code"
                  data-testid="site-code-input"
                  value={values.code || ""}
                  onChange={(e) => set("code", e.target.value.toUpperCase())}
                  placeholder="KYA"
                  maxLength={20}
                />
              </div>
            </div>

            {!isEdit && hasRole(["super_admin"]) && (
              <div className="space-y-1.5">
                <Label>Client *</Label>
                <Select
                  value={values.client_id || ""}
                  onValueChange={(v) => set("client_id", v)}
                >
                  <SelectTrigger data-testid="site-client-select">
                    <SelectValue placeholder="Pick a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </section>

          {/* Section: Address */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Address</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 space-y-1.5">
                <Label>Address line 1</Label>
                <Input
                  data-testid="site-address1-input"
                  value={values.address_line1 || ""}
                  onChange={(e) => set("address_line1", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input
                  data-testid="site-city-input"
                  value={values.city || ""}
                  onChange={(e) => set("city", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>State</Label>
                <Select value={values.state || ""} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="site-state-select">
                    <SelectValue placeholder="Pick a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {AU_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Postcode</Label>
                <Input
                  data-testid="site-postcode-input"
                  value={values.postcode || ""}
                  onChange={(e) => set("postcode", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Timezone</Label>
                <Select value={values.timezone} onValueChange={(v) => set("timezone", v)}>
                  <SelectTrigger data-testid="site-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Section: Operations */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Operations</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Opens at</Label>
                <AuTimeInput
                  data-testid="site-hours-start-input"
                  value={values.operating_hours_start || SITE_DEFAULTS.operating_hours_start}
                  onChange={(e) => set("operating_hours_start", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Closes at</Label>
                <AuTimeInput
                  data-testid="site-hours-end-input"
                  value={values.operating_hours_end || SITE_DEFAULTS.operating_hours_end}
                  onChange={(e) => set("operating_hours_end", e.target.value)}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-1">
                <p className="text-[11px] text-slate-500 leading-snug pt-6">
                  Operating hours block out <strong>all bays</strong> at this site.
                  Slot duration is configured per bay in <em>Edit bay</em>.
                </p>
              </div>
            </div>
            <div className="space-y-1.5 pt-2">
              <Label>Open days</Label>
              <OpenDaysPicker
                value={values.open_days || [1, 2, 3, 4, 5, 6, 7]}
                onChange={(d) => set("open_days", d)}
                testid="site-open-days"
              />
              <p className="text-[11px] text-slate-500">Closed days apply to every bay unless a bay sets its own. Public holidays: use Blackouts.</p>
            </div>
            <div className="space-y-1.5 pt-2">
              <Label>Daily breaks</Label>
              <DailyBreaksEditor value={values.daily_breaks || []} onChange={(b) => set("daily_breaks", b)} testid="site-daily-breaks" />
              <p className="text-[11px] text-slate-500">Repeats every open day (e.g. lunch 12:00–12:30). Bookings can&apos;t overlap a break unless an admin overrides.</p>
            </div>
          </section>

          {"bay_auto_assign" in values && (
            <section className="space-y-1.5">
              <Label>Bay auto-assign</Label>
              <select
                className="h-9 w-full rounded-md border bg-white px-2 text-sm"
                data-testid="site-auto-assign-select"
                value={values.bay_auto_assign === true ? "on" : values.bay_auto_assign === false ? "off" : "inherit"}
                onChange={(e) => set("bay_auto_assign", { on: true, off: false, inherit: null }[e.target.value])}
              >
                <option value="inherit">Inherit tenant setting</option>
                <option value="on">On — auto-assign the first free bay</option>
                <option value="off">Off — booker / staff pick the bay</option>
              </select>
            </section>
          )}

          {/* Section: Booking policy defaults */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Booking policy (site-wide defaults)
            </h3>
            <p className="text-[11px] text-slate-500 leading-snug -mt-1">
              These apply to every bay at this site unless an individual bay
              overrides them. Leave blank for &ldquo;no limit&rdquo;.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Min booking duration (minutes)</Label>
                <Input
                  type="number" min="0" step="5"
                  data-testid="site-min-booking-minutes-input"
                  value={values.min_booking_minutes ?? ""}
                  onChange={(e) => set("min_booking_minutes", e.target.value)}
                  placeholder="e.g. 30"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Max booking duration (hours)</Label>
                <Input
                  type="number" min="1" step="1"
                  data-testid="site-max-booking-hours-input"
                  value={values.max_booking_hours ?? ""}
                  onChange={(e) => set("max_booking_hours", e.target.value)}
                  placeholder="e.g. 4"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Booker minimum booking (minutes)</Label>
                <Input
                  type="number" min="0" step="5"
                  data-testid="site-booker-min-duration-input"
                  value={values.booker_min_duration_minutes ?? ""}
                  onChange={(e) => set("booker_min_duration_minutes", e.target.value)}
                  placeholder="e.g. 60 — blank = no minimum"
                />
                <p className="text-[11px] text-slate-500">
                  Bookers/drivers can&apos;t book shorter than this. Admins (client/site/dock manager) always override.
                </p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>Min lead time (hours before start)</Label>
                  <HelpTip id="site-min-lead">
                    The earliest a booking may start, measured from "right now". Leave blank to use
                    the tenant default from Preferences.
                  </HelpTip>
                </div>
                <Input
                  type="number" min="0" step="1"
                  data-testid="site-min-lead-hours-input"
                  value={values.min_lead_time_hours ?? ""}
                  onChange={(e) => set("min_lead_time_hours", e.target.value)}
                  placeholder="e.g. 16"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>Booking horizon (days ahead)</Label>
                  <HelpTip id="site-horizon">
                    How far in advance drivers and approved bookers may book. Set too short and
                    contract customers can't lock in a month at a time; set too long and the grid
                    fills up with speculative slots. 60 days is a common cold-chain default.
                  </HelpTip>
                </div>
                <Input
                  type="number" min="1" step="1"
                  data-testid="site-max-horizon-days-input"
                  value={values.max_horizon_days ?? ""}
                  onChange={(e) => set("max_horizon_days", e.target.value)}
                  placeholder="e.g. 60"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Max concurrent bookings per bay</Label>
                <Input
                  type="number" min="1" step="1"
                  data-testid="site-max-concurrent-input"
                  value={values.max_concurrent_bookings ?? ""}
                  onChange={(e) => set("max_concurrent_bookings", e.target.value)}
                  placeholder="1"
                />
              </div>
            </div>

            {clientBufferEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label>Turnaround buffer (minutes)</Label>
                    <HelpTip id="site-buffer-default">
                      Default gap left empty after every booking at this site before
                      the next one can start. Each bay can override via the Bay form.
                      Max 180 minutes. Set to 0 to disable at site level.
                    </HelpTip>
                  </div>
                  <Input
                    type="number" min="0" max="180" step="1"
                    data-testid="site-default-buffer-input"
                    value={values.default_buffer_minutes ?? 0}
                    onChange={(e) => set("default_buffer_minutes", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <div className="flex items-start justify-between rounded-lg border bg-white px-3 py-2 gap-3">
                <div>
                  <Label className="text-sm">Require approval</Label>
                  <p className="text-xs text-slate-500">Bookings go to pending_approval first</p>
                </div>
                <Switch
                  checked={!!values.requires_approval}
                  onCheckedChange={(v) => set("requires_approval", v)}
                  data-testid="site-requires-approval-switch"
                />
              </div>
              <div className="flex items-start justify-between rounded-lg border bg-white px-3 py-2 gap-3">
                <div>
                  <Label className="text-sm">Allow multi-day bookings</Label>
                  <p className="text-xs text-slate-500">A booking can span more than one day</p>
                </div>
                <Switch
                  checked={values.allow_multiday !== false}
                  onCheckedChange={(v) => set("allow_multiday", v)}
                  data-testid="site-allow-multiday-switch"
                />
              </div>
              <div className="flex items-start justify-between rounded-lg border bg-white px-3 py-2 gap-3">
                <div>
                  <Label className="text-sm">Require check-in / check-out</Label>
                  <p className="text-xs text-slate-500">Driver must mark arrived and departed</p>
                </div>
                <Switch
                  checked={!!values.requires_check_in_out}
                  onCheckedChange={(v) => set("requires_check_in_out", v)}
                  data-testid="site-requires-checkinout-switch"
                />
              </div>
            </div>
          </section>

          {/* Section: Contact */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Contact name</Label>
                <Input
                  data-testid="site-contact-name-input"
                  value={values.contact_name || ""}
                  onChange={(e) => set("contact_name", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="site-contact-email-input"
                  value={values.contact_email || ""}
                  onChange={(e) => set("contact_email", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  data-testid="site-contact-phone-input"
                  value={values.contact_phone || ""}
                  onChange={(e) => set("contact_phone", e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Section: Status */}
          <section className="flex items-center justify-between rounded-lg border bg-slate-50 px-4 py-3">
            <div>
              <Label className="text-sm">Site active</Label>
              <p className="text-xs text-slate-500">
                Inactive sites are hidden from booking forms but kept in reports.
              </p>
            </div>
            <Switch
              checked={values.is_active}
              onCheckedChange={(v) => set("is_active", v)}
              data-testid="site-active-switch"
            />
          </section>

          {/* Section: PPE pointer */}
          {isEdit && site?.id && (
            <section
              className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 text-xs text-blue-900"
              data-testid="site-ppe-pointer"
            >
              <strong className="font-semibold">PPE requirements</strong> are
              managed per site via the Site Info sections (add a section of type{" "}
              <em>PPE checklist</em>) at{" "}
              <a
                className="underline font-medium"
                href={`/sites/${site.id}/rules`}
                target="_blank"
                rel="noreferrer"
              >
                /sites/{site.id}/rules
              </a>
              . Those PPE items appear on the public site-info page drivers see
              before arrival.
            </section>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="site-save-btn">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create site"}
            </Button>
          </DialogFooter>
        </form>
    </>
  );
}
